import type { StepsProps } from "../../../utils/local-antd";
import isEmpty from "lodash/isEmpty";
import isNil from "lodash/isNil";
import omitBy from "lodash/omitBy";
import React, { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { CreateCampaignDripDto } from "../../../model/campaign/dto/CreateCampaignDripDto";
import { UpdateCampaignDripDto } from "../../../model/campaign/dto/UpdateCampaignDripDto";
import { campaignSliceSelectors } from "../../../redux/campaign/campaign.selector";
import { campaignActions } from "../../../redux/campaign/campaign.slice";
import { useAppDispatch, useAppSelector } from "../../../redux/store";
import { userSliceSelectors } from "../../../redux/user/user.selector";
import { AtiraToast } from "../../../utils/AtiraToast";
import { onSetTypedCRUDErrors } from "../../../utils/onSetTypedCRUDErrors";
import { DripCampaignCreateEditStepBasicInfo } from "../steps/DripCampaignCreateEditStepBasicInfo";
import { DripCampaignCreateEditStepGetStarted } from "../steps/DripCampaignCreateEditStepGetStarted";
import { DripCampaignCreateEditStepSteps } from "../steps/DripCampaignCreateEditStepSteps";
import { useUnsavedChangesWarningModalContext } from "./unsaved-changes-warning-modal/unsaved-changes-warning-modal-context";
import { DrawerWithStepper } from "../../../components/DrawerWithStepper";

type DripCampaignCreateEditDrawerProps = {
  open: boolean;
  onClose: VoidFunction;
};

export const DripCampaignCreateEditDrawer: React.FC<
  DripCampaignCreateEditDrawerProps
> = ({ open = false, onClose }) => {
  const [submitLoading, setSubmitLoading] = useState(false);

  const { openUnsavedChangesModal } = useUnsavedChangesWarningModalContext();
  const { t } = useTranslation();
  const formMethods = useForm<CreateCampaignDripDto | UpdateCampaignDripDto>({
    mode: "onChange",
  });

  const {
    reset,
    formState: { dirtyFields },
    handleSubmit,
  } = formMethods;

  const dispatch = useAppDispatch();

  const selectedCampaign = useAppSelector(
    campaignSliceSelectors.selectSelectedCampaign,
  );
  const userId = useAppSelector(userSliceSelectors.selectLoggedInUserId)!;
  const campaigns = useAppSelector(campaignSliceSelectors.selectCampaigns);
  const canCreateCampaign = !campaigns?.length;

  const editMode =
    !isEmpty(selectedCampaign) && !isEmpty(selectedCampaign?._id);

  const stepperItems: StepsProps["items"] = [
    { title: t("campaigns.dripCampaign.step.getStarted") },
    { title: t("campaigns.dripCampaign.step.basicInfo") },
    { title: t("campaigns.dripCampaign.step.steps") },
  ];

  const handleWarningModalConfirm = () => {
    reset();
    dispatch(campaignActions.setSelectedCampaign(null));
    onClose();
  };

  const onLocalClose = () => {
    if (!isEmpty(dirtyFields)) {
      openUnsavedChangesModal(handleWarningModalConfirm);
      return;
    }

    handleWarningModalConfirm();
  };

  const onCreateCampaign = () => {
    const payload = {
      ...(formMethods.getValues() as CreateCampaignDripDto),
      userId,
    } as CreateCampaignDripDto;

    return dispatch(
      campaignActions.createCampaign(payload as CreateCampaignDripDto),
    ).unwrap();
  };

  const onEditCampaign = () => {
    const dto = formMethods.getValues() as UpdateCampaignDripDto;

    const updateDto = omitBy<UpdateCampaignDripDto>(
      {
        campaignId: dto.campaignId,
        userId,
        active: dto.active,
        formId: dto.formId,
        name: dto.name,
        steps: dto.steps,
      } as UpdateCampaignDripDto,
      isNil,
    ) as unknown as UpdateCampaignDripDto;

    return dispatch(campaignActions.updateCampaign(updateDto)).unwrap();
  };

  const onSubmit = async () => {
    try {
      setSubmitLoading(true);

      if (editMode) {
        await onEditCampaign();
      } else {
        await onCreateCampaign();
      }

      await dispatch(campaignActions.getCampaigns({ userId })).unwrap();

      onClose();
      reset();

      if (editMode) {
        dispatch(campaignActions.setSelectedCampaign(null));
        AtiraToast.success(
          t("campaigns.dripCampaign.edit.success", {
            name: selectedCampaign?.name,
          }),
        );
      } else {
        AtiraToast.success(t("campaigns.dripCampaign.create.success"));
      }

      formMethods.reset();
    } catch (e: any) {
      console.log(e);
      AtiraToast.apiError(e, { duration: 8 });
      onSetTypedCRUDErrors(e, formMethods.setError);
    } finally {
      setSubmitLoading(false);
    }
  };

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (!isEmpty(dirtyFields)) {
        e.preventDefault();
        e.returnValue = "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [dirtyFields]);

  useEffect(() => {
    if (!canCreateCampaign && !editMode) {
      onClose();
    }
  }, [editMode, onClose, canCreateCampaign]);

  useEffect(() => {
    if (open) {
      reset({
        formId: selectedCampaign?.formId,
        active: selectedCampaign?.active,
        campaignId: selectedCampaign?._id,
        name: selectedCampaign?.name,
        steps: selectedCampaign?.steps || [
          {
            body: "",
            hoursAfter: 0,
            subject: "",
            stepNumber: 1,
            ctaText: "",
            ctaUrl: "",
            ctaIcon: undefined,
          },
        ],
        userId,
      });
    }
  }, [open, selectedCampaign, userId, reset]);

  return (
    <FormProvider {...formMethods}>
      <DrawerWithStepper
        open={open}
        onClose={onLocalClose}
        title={t(
          `campaigns.dripCampaign.${editMode ? "edit" : "create"}.title`,
          {
            name: selectedCampaign?.name,
          },
        )}
        submitLoading={submitLoading}
        onSubmit={handleSubmit(onSubmit)}
        steps={[
          <DripCampaignCreateEditStepGetStarted />,
          <DripCampaignCreateEditStepBasicInfo />,
          <DripCampaignCreateEditStepSteps />,
        ]}
        stepperItems={stepperItems}
        editMode={editMode}
      />
    </FormProvider>
  );
};
